import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminReleaseNotesApi, ReleaseNote } from '../services/api';

export default function AdminReleaseNotesPage() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ReleaseNote | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form state
  const [version, setVersion] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [releaseType, setReleaseType] = useState<string>('minor');

  const { data: releaseNotes = [], isLoading } = useQuery({
    queryKey: ['admin', 'release-notes'],
    queryFn: async () => {
      const response = await adminReleaseNotesApi.getAll();
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: adminReleaseNotesApi.create,
    onSuccess: () => {
      enqueueSnackbar('Release Note erstellt', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'release-notes'] });
      handleCloseDialog();
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Erstellen', { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof adminReleaseNotesApi.update>[1] }) =>
      adminReleaseNotesApi.update(id, data),
    onSuccess: () => {
      enqueueSnackbar('Release Note aktualisiert', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'release-notes'] });
      handleCloseDialog();
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Aktualisieren', { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: adminReleaseNotesApi.delete,
    onSuccess: () => {
      enqueueSnackbar('Release Note geloescht', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'release-notes'] });
      setDeleteDialogOpen(false);
      setSelectedNote(null);
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Loeschen', { variant: 'error' });
    },
  });

  const publishMutation = useMutation({
    mutationFn: adminReleaseNotesApi.publish,
    onSuccess: () => {
      enqueueSnackbar('Release Note veroeffentlicht', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'release-notes'] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.response?.data?.error || 'Fehler beim Veroeffentlichen', { variant: 'error' });
    },
  });

  const handleOpenCreate = () => {
    setIsEditing(false);
    setSelectedNote(null);
    setVersion('');
    setTitle('');
    setContent('');
    setReleaseType('minor');
    setDialogOpen(true);
  };

  const handleOpenEdit = (note: ReleaseNote) => {
    setIsEditing(true);
    setSelectedNote(note);
    setVersion(note.version);
    setTitle(note.title);
    setContent(note.content);
    setReleaseType(note.releaseType);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedNote(null);
    setVersion('');
    setTitle('');
    setContent('');
    setReleaseType('minor');
  };

  const handleSave = () => {
    if (isEditing && selectedNote) {
      updateMutation.mutate({
        id: selectedNote.id,
        data: { version, title, content, releaseType },
      });
    } else {
      createMutation.mutate({ version, title, content, releaseType });
    }
  };

  const getReleaseTypeColor = (type: string) => {
    switch (type) {
      case 'major':
        return 'error';
      case 'minor':
        return 'primary';
      case 'patch':
        return 'success';
      default:
        return 'default';
    }
  };

  const getReleaseTypeLabel = (type: string) => {
    switch (type) {
      case 'major':
        return 'Major';
      case 'minor':
        return 'Minor';
      case 'patch':
        return 'Patch';
      default:
        return type;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Release Notes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Neue Release Note
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Titel</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : releaseNotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Keine Release Notes vorhanden
                  </TableCell>
                </TableRow>
              ) : (
                releaseNotes.map((note: ReleaseNote) => (
                  <TableRow key={note.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>v{note.version}</Typography>
                    </TableCell>
                    <TableCell>{note.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={getReleaseTypeLabel(note.releaseType)}
                        color={getReleaseTypeColor(note.releaseType) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={note.isPublished ? 'Veroeffentlicht' : 'Entwurf'}
                        color={note.isPublished ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(note.createdAt).toLocaleDateString('de-DE')}
                    </TableCell>
                    <TableCell align="right">
                      {!note.isPublished && (
                        <Tooltip title="Veroeffentlichen">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => publishMutation.mutate(note.id)}
                            disabled={publishMutation.isPending}
                          >
                            <PublishIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Bearbeiten">
                        <IconButton size="small" onClick={() => handleOpenEdit(note)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Loeschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedNote(note);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isEditing ? 'Release Note bearbeiten' : 'Neue Release Note'}
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="1.2.0"
                sx={{ width: 150 }}
                required
              />
              <FormControl sx={{ width: 150 }}>
                <InputLabel>Typ</InputLabel>
                <Select
                  value={releaseType}
                  label="Typ"
                  onChange={(e) => setReleaseType(e.target.value)}
                >
                  <MenuItem value="major">Major</MenuItem>
                  <MenuItem value="minor">Minor</MenuItem>
                  <MenuItem value="patch">Patch</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Titel"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Neue Features und Verbesserungen"
                fullWidth
                required
              />
            </Box>
            <TextField
              label="Inhalt (Markdown)"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              multiline
              rows={12}
              fullWidth
              required
              placeholder={`## Neue Features
- Feature 1
- Feature 2

## Verbesserungen
- Verbesserung 1

## Fehlerbehebungen
- Bug Fix 1`}
            />
            <Alert severity="info">
              Der Inhalt wird als Markdown formatiert. Verwenden Sie ## fuer Ueberschriften und - fuer Listen.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!version || !title || !content || createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) ? (
              <CircularProgress size={20} />
            ) : isEditing ? (
              'Speichern'
            ) : (
              'Erstellen'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Release Note loeschen</DialogTitle>
        <DialogContent>
          <Typography>
            Moechten Sie die Release Note <strong>v{selectedNote?.version}</strong> wirklich loeschen?
          </Typography>
          {selectedNote?.isPublished && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Diese Release Note ist bereits veroeffentlicht. Das Loeschen kann Auswirkungen auf Benutzer haben.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedNote && deleteMutation.mutate(selectedNote.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Loeschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
